import {
  DragDropState,
  DragSource,
  DropTarget,
} from '@wandb/weave/common/containers/DragDropContainer';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import produce from 'immer';
import _ from 'lodash';
import React, {memo, useCallback, useMemo, useRef, useState} from 'react';

import {trackWorkspaceDragAndDrop} from '../../services/analytics/dragAndDropEvent';
import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelTypes from '../../state/views/panel/types';
import {usePanelBankSectionConfig} from '../../state/views/panelBankSectionConfig/hooks';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {
  isDraggingWithinSection,
  isPanel,
  isPanelBankSection,
} from '../../util/panelbank';
import {PANEL_BANK_HIDDEN_SECTION_NAME} from '../../util/panelbankTypes';
import {useDetectChanges} from '../../util/react-help/detectReactPropChanges';
import {useRunSelectorResizingContextMaybe} from '../MultiRunWorkspace/RunSelectorResizingContext';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {useSectionSettingsContext} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {usePanelBankSectionIsOpen} from './hooks/panelBankSectionIsOpenHooks';
import {logDragVital} from './logDragVital';
import {PanelBankFlowSection} from './PanelBankFlowSection';
import {PanelBankSectionHeader} from './PanelBankSectionHeader';
import {getPanelsToTrack, TimerEntry} from './PanelBankTiming';
import {usePanelBankWidthContext} from './PanelBankWidthContext';
import type {SectionMetadata} from './types';

type PanelBankSectionProps = SectionMetadata & {
  isProjectReadOnly?: boolean;
  // query: Query.Query;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  defaultMoveToSectionRef?: PanelBankSectionConfigTypes.Ref;

  showPinningBorder?: boolean;
  isFirstSection?: boolean;
  moveSectionBefore(
    moveRef: PanelBankSectionConfigTypes.Ref,
    beforeSectionRef: PanelBankSectionConfigTypes.Ref
  ): void;
  movePanel(
    panelRef: PanelTypes.Ref,
    fromSectionRef: PanelBankSectionConfigTypes.Ref,
    toSectionRef: PanelBankSectionConfigTypes.Ref,
    toIndex: number
  ): void;
};

function usePanelBankSection({
  panelBankSectionConfigRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
}) {
  const {isSearching} = usePanelSearchDebouncedContext();

  const {sectionIsOpen, shouldRenderSection} = usePanelBankSectionIsOpen({
    panelBankSectionConfigRef,
  });
  const panelBankSectionConfig = usePanelBankSectionConfig(
    panelBankSectionConfigRef
  );
  return useMemo(
    () => ({
      isSearching,
      panelBankSectionConfig,
      sectionIsOpen,
      shouldRenderSection,
    }),
    [isSearching, panelBankSectionConfig, sectionIsOpen, shouldRenderSection]
  );
}

/**
 * @deprecated This will soon be deprecated and be swapped out for PanelBankSections2.
 * If you need to make changes to this area, please let Joyce know.
 */
export const PanelBankSection = memo(function PanelBankSection(
  props: PanelBankSectionProps
) {
  const {
    panelBankSectionConfigRef,
    activePanelRefs,
    inactivePanelRefs,
    panelSearchResults,
    panelSearchMatchCount = 0,
    isSectionNameSearchMatch,
    moveSectionBefore,
    movePanel,
    isProjectReadOnly,
    defaultMoveToSectionRef,
    showPinningBorder,
    isFirstSection,
  } = props;
  const fullWorkspaceRefsContext = useWorkspaceRefsContext();
  const {panelBankConfigRef} = fullWorkspaceRefsContext;

  const sectionComponentRef = useRef<HTMLDivElement>(null);

  const {
    isSearching,
    panelBankSectionConfig,
    sectionIsOpen,
    shouldRenderSection,
  } = usePanelBankSection({
    panelBankSectionConfigRef,
  });

  const {
    targetSectionRef: workspaceDrawerTarget,
    shouldHidePanelContents,
    targetPanelRef,
  } = useWorkspaceDrawerContext();
  const isWorkspaceDrawerTarget = useMemo(
    () =>
      _.isEqual(panelBankSectionConfigRef, workspaceDrawerTarget) &&
      targetPanelRef == null,
    [panelBankSectionConfigRef, workspaceDrawerTarget, targetPanelRef]
  );

  const runSelectorResizingContext = useRunSelectorResizingContextMaybe();

  const normalizedPanelBankSectionConfig = ViewHooks.usePart(
    panelBankSectionConfigRef
  );

  const {inheritedSettings} = useSectionSettingsContext();

  const activePanelCount = activePanelRefs.length;
  const totalPanelCount = panelBankSectionConfig.panels.length;

  const {panelBankWidth} = usePanelBankWidthContext();
  const panelBankSectionRefs =
    ViewHooks.usePart(panelBankConfigRef).sectionRefs;
  const isLastSection = _.isEqual(
    panelBankSectionConfigRef,
    panelBankSectionRefs[panelBankSectionRefs.length - 1]
  );
  const isHiddenPanelsSection =
    isLastSection &&
    panelBankSectionConfig.name === PANEL_BANK_HIDDEN_SECTION_NAME;

  // Note: we are moving towards one layout type. This is a temporary change so all sections will use the standard layout.
  // We're going to ignore custom grid layout entirely but preserve it in case we need to rollback.
  const sectionLayoutType = 'flow';

  const sectionName = panelBankSectionConfig.name;

  const trackedPanels = useMemo(() => {
    return getPanelsToTrack(
      panelBankSectionConfig,
      normalizedPanelBankSectionConfig.panelRefs,
      isSearching ? panelSearchResults : undefined
    );
  }, [
    panelBankSectionConfig,
    normalizedPanelBankSectionConfig.panelRefs,
    isSearching,
    panelSearchResults,
  ]);
  // BEWARE AND BEWARE AND BEWARE:
  // THIS IS DEAD CODE. THE 'HAS FIRST SECTION LOADED' CODE IS ALL BROKEN RIGHT
  // NOW AND NEEDS TO BE FIXED. I AM LEAVING THIS FOR NOW BECAUSE IT'S MORE
  // DISRUPTIVE TO REMOVE IT --NH
  const [loadTimes, setLoadTimes] = useState<TimerEntry[]>(trackedPanels);
  const stopTimerCallback = useCallback(
    (name: string, data: Record<string, unknown> | undefined) => {
      if (data && data.duration != null) {
        setLoadTimes(prev =>
          produce(prev, draft => {
            const loadingPanel = draft.find(p => p.id === name);
            if (loadingPanel) {
              loadingPanel.duration = data.duration as number;
            }
          })
        );
      }
    },
    [setLoadTimes]
  );

  const onSectionDragStart = useCallback((ctx: DragDropState) => {
    logDragVital('drag_start', {
      alwaysLog: true,
      activeDragType: isPanel(ctx.dragRef) ? 'panel' : 'section',
    });
  }, []);

  const shouldRenderContent =
    !shouldHidePanelContents && !runSelectorResizingContext?.isResizing;

  useDetectChanges('PanelBankSection', {
    defaultMoveToSectionRef,
    isFirstSection,
    isProjectReadOnly,
    isSearching,
    normalizedPanelBankSectionConfig,
    npbscPanelRefs: normalizedPanelBankSectionConfig.panelRefs,
    panelBankSectionConfig,
    panelSearchResults,
    sectionIsOpen,
    shouldHidePanelContents,
    shouldRenderSection,
    ...props,
    ...fullWorkspaceRefsContext,
  });

  return (
    <Tailwind>
      <div
        className={
          showPinningBorder || isWorkspaceDrawerTarget
            ? 'border-[5px] border-gold-300'
            : ''
        }
        data-test={`panel-bank-section-[${sectionName}]`}
        ref={sectionComponentRef}>
        <DropTarget
          isValidDropTarget={({dragRef, dragData}) => {
            return (
              isPanelBankSection(dragRef) ||
              (isPanel(dragRef) &&
                !isDraggingWithinSection(panelBankSectionConfigRef, dragData))
            );
          }}
          partRef={panelBankSectionConfigRef}
          getClassName={({dragRef, dropRef}) => {
            const elementClassNames = ['panel-bank__section'];
            if (!sectionIsOpen) {
              elementClassNames.push('collapsed');
            }
            if (isHiddenPanelsSection) {
              elementClassNames.push('panel-bank__section__hidden-panels');
            }
            if (dragRef != null && isPanel(dragRef)) {
              // always added when dragging a panel, even if the panel is not in this section
              elementClassNames.push('dragging-panel');
            }
            if (
              dropRef != null &&
              !_.isEqual(dragRef, dropRef) &&
              _.isEqual(panelBankSectionConfigRef, dropRef)
            ) {
              if (isPanelBankSection(dragRef)) {
                elementClassNames.push('active-section-drop-target');
              }
              if (
                isPanel(dragRef) &&
                (!sectionIsOpen || totalPanelCount === 0)
              ) {
                elementClassNames.push('active-box-drop-target');
              }
            }
            return elementClassNames.join(' ');
          }}
          onDrop={({dragRef, dragData}) => {
            // Drag panel onto section
            if (isPanel(dragRef) && dragData) {
              logDragVital('drag_end', {
                action: 'Drag panel to different section',
                alwaysLog: true,
                activeDragType: 'panel',
              });
              movePanel(
                dragRef,
                dragData.fromSectionRef,
                panelBankSectionConfigRef,
                0
              );
              trackWorkspaceDragAndDrop({
                action: 'Drag panel to different section',
                layoutType: 'flow',
              });
            }
            // Drag section onto section
            if (isPanelBankSection(dragRef)) {
              if (!_.isEqual(dragRef, panelBankSectionConfigRef)) {
                logDragVital('drag_end', {
                  action: 'Reorder sections',
                  alwaysLog: true,
                  activeDragType: 'section',
                });
                moveSectionBefore(dragRef, panelBankSectionConfigRef);
                trackWorkspaceDragAndDrop({
                  action: 'Reorder sections',
                  layoutType: 'flow',
                });
              }
            }
          }}>
          <DragSource
            partRef={panelBankSectionConfigRef}
            onDragStart={onSectionDragStart}>
            <PanelBankSectionHeader
              activePanelCount={activePanelCount}
              isProjectReadOnly={isProjectReadOnly}
              isSectionNameSearchMatch={isSectionNameSearchMatch}
              panelBankSectionConfigRef={panelBankSectionConfigRef}
              panelBankSectionRefs={panelBankSectionRefs}
              panelSearchMatchCount={panelSearchMatchCount}
              sectionComponentRef={sectionComponentRef}
              sectionLayoutType={sectionLayoutType}
              sectionName={sectionName}
            />
            {/* We're using this wrapper with {display: none} because a) we don't want to render the section if it's never opened,
           and b) we don't want to re-mount an open section if we close and re-open it. */}
            <div style={sectionIsOpen ? {} : {display: 'none'}}>
              {shouldRenderSection ? (
                <PanelBankFlowSection
                  activePanelRefs={activePanelRefs}
                  defaultMoveToSectionRef={defaultMoveToSectionRef}
                  inactivePanelRefs={inactivePanelRefs}
                  inheritedSettings={inheritedSettings}
                  isSectionNameSearchMatch={isSectionNameSearchMatch}
                  movePanelBetweenSections={movePanel}
                  panelBankSectionConfigRef={panelBankSectionConfigRef}
                  panelBankWidth={panelBankWidth}
                  panelSearchMatchCount={panelSearchMatchCount}
                  panelSearchResults={panelSearchResults}
                  isProjectReadOnly={isProjectReadOnly}
                  // when toggling workspace drawer or resizing run selector,
                  // redrawing panels as they resize can be very perf intensive
                  // and cause jumpy/laggy behavior. to avoid this problem,
                  // hide panel contents while panel bank is resizing.
                  // redrawing panels as they resize can be very perf intensive
                  // when toggling workspace drawer or resizing run selector,
                  shouldRenderContent={shouldRenderContent}
                />
              ) : null}
            </div>
          </DragSource>
        </DropTarget>
        <div className="panel-bank__divider" />
      </div>
    </Tailwind>
  );
});
